import { createContext, useContext } from 'react';
import type { callEndpoint, EndpointDef } from '@ooh/contracts';
import type { z } from 'zod';

export interface ApiClient {
  call: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
    options?: { signal?: AbortSignal },
  ) => ReturnType<typeof callEndpoint<TDef>>;
  /** Same as `call` without credentials or a session, for anonymous token-scoped endpoints (public offer link). */
  callPublic: ApiClient['call'];
  /**
   * Multipart upload of one file (plus optional text fields) to a POST endpoint; `callEndpoint` cannot send FormData.
   * `pathParams` fill the `:name` placeholders of the endpoint path.
   */
  upload: <TDef extends EndpointDef>(
    def: TDef,
    file: File,
    fields?: Record<string, string>,
    pathParams?: Record<string, string | number>,
  ) => Promise<z.output<TDef['responseSchema']>>;
  /** GET of a binary body (template XLSX); `callEndpoint` only reads JSON. */
  download: (def: EndpointDef, query?: Record<string, string>) => Promise<Blob>;
}

export const ApiClientContext = createContext<ApiClient | null>(null);

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (client === null) throw new Error('useApiClient must be used inside <AppUiProvider>');
  return client;
}
